import React from "react";
import { useTranslation } from "react-i18next";
import moment, { Moment } from "moment";
import "moment/locale/en-gb";
import "moment/locale/de";
import { DatePicker } from "../../../../../components/ui/datepicker";
import { isOutsideBookableDays } from "../../hooks/useBookingWindow";

// Set Monday as the first day of the week for all locales
moment.updateLocale("en-gb", { week: { dow: 1 } });
moment.updateLocale("de", { week: { dow: 1 } });

interface BookingDatePickerProps {
  value: Moment | null;
  onChange: (date: Moment | null) => void;
  shouldDisableDate: (date: Moment | null) => boolean;
  availableDates: string[];
  label: string;
  disabled?: boolean;
  minDate?: Moment | null;
  /**
   * Latest selectable date — the tenant's configured booking horizon for this
   * use case, from `useBookingWindow`.
   *
   * This used to be a hardcoded `moment().add(18, "month")` inside the
   * component, which was the THIRD copy of a horizon the rest of the app
   * computed as one month: it never bound anything, because `availableDates`
   * already stopped a month out, and a reader could not tell which of the two
   * numbers was the rule. Consolidated onto the configured value, which
   * defaults to one month for every use case that renders this picker — so the
   * effective behaviour is unchanged.
   *
   * Optional, so a caller that has not been given a window falls back to
   * `availableDates` alone, exactly as before.
   */
  maxDate?: Moment | null;
}

const BookingDatePicker: React.FC<BookingDatePickerProps> = React.memo(
  (props: BookingDatePickerProps) => {
    const { t } = useTranslation();

    // Parse available dates from string format to moment objects
    const parseDates = (dates: string[]): Moment[] =>
      dates.map((date) => moment(date, "DD.MM.YYYY"));
    const parsedDates = parseDates(props.availableDates);

    // Validation: disable dates that are not in availableDates
    const isDateDisabled = (date: Date): boolean => {
      const momentDate = moment(date);
      
      // Before minDate or past the configured booking horizon — compared as
      // calendar-day labels (picked day vs the station's day of each bound).
      if (isOutsideBookableDays(momentDate, props.minDate, props.maxDate)) {
        return true;
      }
      
      // Check if date is in availableDates
      return !parsedDates.some((parsedDate) => parsedDate.isSame(momentDate, "day"));
    };

    // Convert moment to Date for ShadCN DatePicker
    const dateValue = props.value ? props.value.toDate() : null;

    const handleDateChange = (date: Date | null) => {
      const momentDate = date ? moment(date) : null;
      props.onChange(momentDate);
    };

    return (
      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-slate-900">
          {props.label}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <DatePicker
          value={dateValue}
          onChange={handleDateChange}
          disabled={props.disabled}
          isDateDisabled={isDateDisabled}
          placeholder={t("general.date")}
          buttonClassName="w-full h-14 text-base border rounded-xl bg-white text-slate-900 border-black"
          aria-label={props.label}
        />
      </div>
    );
  }
);

export default BookingDatePicker;
